// =============================================================================
// waitlistCore.js - pure helpers for the Waitlist screen (no React, no
// network; unit-tested in src/lib/__tests__/waitlist.test.mjs). The server
// calls are in waitlist.js.
//
// Times are read in the PRACTICE time zone (practices.timezone), the same way
// the offer generator does (migration 20261013100000). A visit at 7pm Eastern
// is "Monday" even though it is Tuesday in UTC.
// =============================================================================

export const DEFAULT_TZ = "America/New_York";
export const DOW_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
export const DOW_LABELS = { mon: "Mon", tue: "Tue", wed: "Wed", thu: "Thu", fri: "Fri", sat: "Sat", sun: "Sun" };
export const WINDOW_OPTIONS = [
  { value: "", label: "Any time" },
  { value: "Morning", label: "Morning (before noon)" },
  { value: "Afternoon", label: "Afternoon (noon - 5pm)" },
  { value: "Evening", label: "Evening (after 5pm)" },
];
export const REMOVE_REASONS = ["Booked elsewhere", "No longer needs a visit", "Could not reach", "Patient asked to be removed", "Other"];
export const ACTIVE_STATUSES = ["Open", "Contacted"];

// { ymd, hour, minute, dow } of an instant in a time zone.
export function tzParts(iso, tz = DEFAULT_TZ) {
  const d = iso instanceof Date ? iso : new Date(iso);
  const parts = {};
  new Intl.DateTimeFormat("en-US", {
    timeZone: tz || DEFAULT_TZ, year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hourCycle: "h23", weekday: "short",
  }).formatToParts(d).forEach((p) => { parts[p.type] = p.value; });
  return {
    ymd: parts.year + "-" + parts.month + "-" + parts.day,
    hour: Number(parts.hour) % 24,
    minute: Number(parts.minute),
    dow: (parts.weekday || "").slice(0, 3).toLowerCase(),
  };
}

export function todayYmd(tz = DEFAULT_TZ, now = new Date()) { return tzParts(now, tz).ymd; }

export function fmtSlot(iso, tz = DEFAULT_TZ) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { timeZone: tz, weekday: "short", month: "short", day: "numeric" }) +
    " at " + d.toLocaleTimeString("en-US", { timeZone: tz, hour: "numeric", minute: "2-digit" });
}
export function fmtTime(iso, tz = DEFAULT_TZ) {
  return iso ? new Date(iso).toLocaleTimeString("en-US", { timeZone: tz, hour: "numeric", minute: "2-digit" }) : "";
}
// YYYY-MM-DD as a calendar date (no time zone shift).
export function fmtYmd(ymd) {
  if (!ymd || !/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return ymd || "";
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

// Free-text time of day -> does this hour fit? Mirrors waitlist_window_ok().
export function windowFits(pref, hour) {
  const w = String(pref || "").toLowerCase();
  if (!/(morning|afternoon|evening|\bam\b|\bpm\b)/.test(w)) return true;
  return (/(morning|\bam\b)/.test(w) && hour < 12)
    || (/afternoon/.test(w) && hour >= 12 && hour < 17)
    || (/evening/.test(w) && hour >= 17)
    || (/\bpm\b/.test(w) && hour >= 12);
}

export const isRemoved = (e) => !!(e && e.removed_at);
export const isActiveEntry = (e) => !!e && ACTIVE_STATUSES.includes(e.status) && !isRemoved(e);
export function entryStatusLabel(e) {
  if (isRemoved(e)) return "Removed";
  if (e.status === "Filled" || e.status === "Accepted") return "Booked";
  return e.status;
}
export function entryStatusVariant(e) {
  const l = entryStatusLabel(e);
  return { Open: "amber", Contacted: "blue", Booked: "green", Removed: "neutral", Declined: "neutral", Expired: "neutral" }[l] || "neutral";
}

const holds = (status) => !["Cancelled", "Rescheduled", "No Show"].includes(status || "");

// Does this entry fit this open slot? -> { ok, why } (why = first reason not).
// slot: { start_at, provider_id, appt_type, patient_id }
export function matchEntryToSlot(entry, slot, tz = DEFAULT_TZ) {
  if (!isActiveEntry(entry)) return { ok: false, why: "not active" };
  if (!slot || !slot.start_at) return { ok: false, why: "no slot" };
  if (slot.patient_id && entry.patient_id === slot.patient_id) return { ok: false, why: "cancelled this slot" };
  if (entry.appt_type !== slot.appt_type) return { ok: false, why: "different visit type" };
  if (entry.provider_id && slot.provider_id && entry.provider_id !== slot.provider_id) return { ok: false, why: "different provider" };
  const p = tzParts(slot.start_at, tz);
  if (entry.earliest_acceptable && p.ymd < entry.earliest_acceptable) return { ok: false, why: "before earliest date" };
  if (entry.latest_acceptable && p.ymd > entry.latest_acceptable) return { ok: false, why: "after latest date" };
  if (Array.isArray(entry.preferred_days) && entry.preferred_days.length && !entry.preferred_days.includes(p.dow)) return { ok: false, why: "not a preferred day" };
  if (!windowFits(entry.preferred_window, p.hour)) return { ok: false, why: "outside preferred time" };
  const cur = entry.current_appt;
  if (cur && cur.start_at && holds(cur.status) && new Date(cur.start_at) <= new Date(slot.start_at)) return { ok: false, why: "not sooner than their visit" };
  return { ok: true, why: null };
}

// "Find slot match": a date (no time) the user typed in.
export function matchHypothetical(entry, s) {
  if (!isActiveEntry(entry) || !s || !s.date) return false;
  if (s.appt_type && entry.appt_type !== s.appt_type) return false;
  if (entry.provider_id && s.provider_id && entry.provider_id !== s.provider_id) return false;
  if (entry.earliest_acceptable && s.date < entry.earliest_acceptable) return false;
  if (entry.latest_acceptable && s.date > entry.latest_acceptable) return false;
  if (Array.isArray(entry.preferred_days) && entry.preferred_days.length) {
    const dow = DOW_KEYS[new Date(s.date + "T12:00:00").getDay()];
    if (!entry.preferred_days.includes(dow)) return false;
  }
  return true;
}

export function waitingDays(entry, now = new Date()) {
  if (!entry || !entry.created_at) return 0;
  return Math.max(0, Math.floor((now.getTime() - new Date(entry.created_at).getTime()) / 86400000));
}
export const agingVariant = (days) => (days >= 14 ? "red" : days >= 7 ? "amber" : null);

// Highest priority first; ties go to whoever has waited longest.
export function rankEntries(list, sort = "priority") {
  const by = (a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0);
  return (list || []).slice().sort((a, b) => sort === "waiting"
    ? by(a, b) || (b.priority_score || 0) - (a.priority_score || 0)
    : (b.priority_score || 0) - (a.priority_score || 0) || by(a, b));
}

// What the automatic text is doing for one offer.
// -> { kind: "replied" | "texting" | "no_reply" | "paused" | "contacted" | "queued" | "none", minutesLeft }
export function offerState(offer, now = new Date()) {
  if (!offer) return { kind: "none" };
  if (offer.classified_intent === "AMBIGUOUS" && offer.inbound_reply_text) return { kind: "replied" };
  if (offer.cascade_sent_at) {
    const exp = offer.cascade_expires_at ? new Date(offer.cascade_expires_at) : null;
    if (exp && exp > now) return { kind: "texting", minutesLeft: Math.max(1, Math.ceil((exp - now) / 60000)) };
    return { kind: "no_reply" };
  }
  if (offer.status === "Contacted") return { kind: "contacted" };
  if (offer.cascade_paused) return { kind: "paused" };
  if (offer.status === "Pending") return { kind: "queued" };
  return { kind: "none" };
}

// A cancelled slot is no longer open once another booked visit covers it.
export function slotIsRebooked(slot, booked) {
  const s = new Date(slot.start_at).getTime(), e = new Date(slot.end_at || slot.start_at).getTime();
  return (booked || []).some((a) => a.id !== slot.id && a.provider_id === slot.provider_id && holds(a.status)
    && new Date(a.start_at).getTime() < e && new Date(a.end_at || a.start_at).getTime() > s);
}

export function entryMatchesQuery(e, q) {
  const words = String(q || "").toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return true;
  const p = e.patients || {};
  const hay = [p.first_name, p.last_name, p.mrn, e.notes, e.appt_type, e.providers && e.providers.last_name].filter(Boolean).join(" ").toLowerCase();
  return words.every((w) => hay.includes(w));
}

export const TABS = [
  { key: "open", label: "Waiting" },
  { key: "booked", label: "Booked" },
  { key: "closed", label: "Closed" },
  { key: "all", label: "All" },
];
export function inTab(e, tab) {
  if (tab === "open") return isActiveEntry(e);
  if (tab === "booked") return !isRemoved(e) && (e.status === "Filled" || e.status === "Accepted");
  if (tab === "closed") return isRemoved(e) || e.status === "Declined" || e.status === "Expired";
  return true;
}

const ERRORS = {
  not_authorized: "You don't have permission to do that.",
  not_found: "That waitlist item no longer exists. The list has been refreshed.",
  slot_taken: "Someone else already took that time. The list has been refreshed.",
  slot_past: "That time has already passed.",
  patient_busy: "The patient already has a visit at that time.",
  patient_inactive: "That patient is no longer active.",
  offer_closed: "That offer is already closed.",
  entry_closed: "That patient is no longer on the waitlist.",
  reason_required: "Pick a reason.",
};
export function waitlistError(e) {
  const m = String((e && (e.message || e)) || "");
  const key = Object.keys(ERRORS).find((k) => m === k || m.includes(k));
  return key ? ERRORS[key] : (m || "Something went wrong.");
}

// Undo of a waitlist Book (waitlist_unfill_offer, migration
// 20270217110000). The server refuses once 10 minutes have passed, when
// anything it would put back changed since (visit edited or checked in, the
// freed later time booked, a reply came in, ...), or when the booking was
// someone else's. Staff do not need the reason: the way out is the same.
// The undone visit stays (Cancelled, for the record) at the same time as the
// original open slot; the server removes its offers and the screen does not
// list it, so the time is not shown twice.
export const UNDONE_BOOKING_REASON = "Waitlist booking undone";
export const UNDO_REFUSED_TEXT = "This booking can no longer be undone - cancel it from the Schedule.";
const UNDO_REFUSALS = ["undo_expired", "undo_changed", "undo_not_found", "not_authorized", "not_found"];

export function unfillErrorText(e) {
  const m = String((e && (e.message || e)) || "");
  if (UNDO_REFUSALS.some((k) => m === k || m.includes(k))) return UNDO_REFUSED_TEXT;
  return "Could not undo the booking (" + (m || "unknown error") + "). If it still shows, cancel it from the Schedule.";
}

// Toast after a successful undo. texts_sent: the booking freed the patient's
// later time and the automatic waitlist text for it already went out before
// the undo (texts still waiting were cancelled by the server, never sent).
export function unfillDoneText(res) {
  const sent = Number((res && res.texts_sent) || 0);
  let t = "Booking undone.";
  if (res && res.restored_visit) t += " Their later visit is back.";
  if (sent > 0) {
    t += sent === 1
      ? " An automatic text offering their later time had already gone out; that offer is now closed."
      : " " + sent + " automatic texts offering their later time had already gone out; those offers are now closed.";
  }
  return t;
}

// ---- "Undo booking" link (10-minute window) ---------------------------------
// The Undo toast after a Book lasts a few seconds, but waitlist_unfill_offer
// accepts an undo for 10 minutes from the staff user who booked. Who booked
// and when lives in waitlist_fill_undo, which the browser cannot read (no
// grant, no policy), so the screen remembers its own bookings: ids and a time
// only, never a name, kept in sessionStorage for this tab. The server still
// decides; a refusal reads as UNDO_REFUSED_TEXT. bookedAt is taken BEFORE the
// fill call, so the link always closes a little before the server would.
export const UNDO_LINK_WINDOW_MS = 10 * 60 * 1000;
export const UNDO_LINK_CONFIRM = {
  title: "Undo this booking?",
  body: "The visit goes back to its earlier time and the open time is offered again.",
  confirmLabel: "Undo booking",
};

const msLeft = (rec, now) => (rec ? Number(rec.bookedAt) + UNDO_LINK_WINDOW_MS - now : 0);

// Drops records that are expired or malformed (anything hand-edited in
// storage must not break the screen).
export function pruneRecentBooks(list, now) {
  return (Array.isArray(list) ? list : []).filter((r) =>
    r && typeof r.offerId === "string" && typeof r.apptId === "string" && typeof r.userId === "string"
    && Number.isFinite(Number(r.bookedAt)) && msLeft(r, now) > 0);
}

// Adds a booking (one record per visit, the newest wins), pruning old ones.
export function addRecentBook(list, rec, now) {
  const keep = pruneRecentBooks(list, now).filter((r) => r.apptId !== rec.apptId);
  return [...keep, { offerId: rec.offerId, apptId: rec.apptId, userId: rec.userId, bookedAt: Number(rec.bookedAt) }].slice(-20);
}

export function removeRecentBook(list, apptId, now) {
  return pruneRecentBooks(list, now).filter((r) => r.apptId !== apptId);
}

// The record that lets this user undo this visit right now, or null. Another
// user signed in to the same tab never sees it (the server would refuse).
export function recentBookFor(list, apptId, userId, now) {
  if (!apptId || !userId) return null;
  return pruneRecentBooks(list, now).find((r) => r.apptId === apptId && r.userId === userId) || null;
}

// Whole minutes left, rounded up (9:59 left reads "10 min left"); 0 = closed.
export function undoMinutesLeft(rec, now) {
  const ms = msLeft(rec, now);
  return ms > 0 ? Math.ceil(ms / 60000) : 0;
}

// Milliseconds until the minute count changes (or the link closes), so the
// countdown re-renders exactly when its text would change.
export function msUntilNextMinute(rec, now) {
  const ms = msLeft(rec, now);
  if (ms <= 0) return 0;
  const r = ms % 60000;
  return r === 0 ? 60000 : r;
}

export function undoLinkLabel(minutes) {
  return "Undo booking - " + minutes + " min left";
}

// ---- Round 7: the Waitlist as a work queue ------------------------------------
// Open times are the first view; each row shows its best fit and the detail
// lists everyone who fits. These helpers turn the matching rules above into
// the short words on screen.

// The four views. "times" lists open times; the other three are TABS keys
// for waitlist entries (the old "All" pill went: every entry is in one of
// Waiting, Booked or Closed).
export const VIEWS = [
  { key: "times", label: "Open times" },
  { key: "open", label: "Waiting" },
  { key: "booked", label: "Booked" },
  { key: "closed", label: "Closed" },
];

// "morning" -> "mornings"; free text ("after 3pm") stays as typed.
function windowWords(pref) {
  const w = String(pref || "").trim().toLowerCase();
  return /^(morning|afternoon|evening)$/.test(w) ? w + "s" : w;
}
const listWords = (xs) => (xs.length <= 1 ? xs.join("") : xs.slice(0, -1).join(", ") + " and " + xs[xs.length - 1]);

// Why a patient listed for an open time does not quite fit it, in plain
// words ("Prefers mornings, this time is 1:00 PM"), or null when they fit.
// A patient who does not fit is only listed when they already have an offer
// for the time (called, texted or picked by hand), so staff see the catch
// before they book.
export function fitNote(entry, slot, tz = DEFAULT_TZ) {
  const r = matchEntryToSlot(entry, slot, tz);
  if (r.ok || r.why === "not active" || r.why === "no slot") return null;
  const p = tzParts(slot.start_at, tz);
  switch (r.why) {
    case "outside preferred time": return "Prefers " + windowWords(entry.preferred_window) + ", this time is " + fmtTime(slot.start_at, tz);
    case "not a preferred day": return "Prefers " + listWords((entry.preferred_days || []).map((d) => DOW_LABELS[d] || d)) + ", this time is a " + (DOW_LABELS[p.dow] || p.dow);
    case "different provider": return "Asked for a different provider";
    case "different visit type": return "Waiting for a " + entry.appt_type + " visit";
    case "before earliest date": return "Can only come from " + fmtYmd(entry.earliest_acceptable);
    case "after latest date": return "Wanted a time by " + fmtYmd(entry.latest_acceptable);
    case "not sooner than their visit": return "Their visit on " + fmtSlot(entry.current_appt.start_at, tz) + " is sooner";
    case "cancelled this slot": return "Cancelled this time";
    default: return null;
  }
}

// Length of an open time in minutes (15-minute slots when the end is missing).
export function slotMinutes(slot) {
  if (!slot) return 0;
  const s = new Date(slot.start_at).getTime(), e = new Date(slot.end_at || 0).getTime();
  if (Number.isFinite(s) && Number.isFinite(e) && e > s) return Math.round((e - s) / 60000);
  return (Number(slot.duration_slots) || 0) * 15;
}

// "opened 2 hours ago" for a cancelled visit, from when it was cancelled.
export function openedAgo(iso, now = new Date()) {
  if (!iso) return "";
  const mins = Math.floor((now.getTime() - new Date(iso).getTime()) / 60000);
  if (!Number.isFinite(mins)) return "";
  if (mins < 2) return "opened just now";
  if (mins < 60) return "opened " + mins + " min ago";
  const h = Math.floor(mins / 60);
  if (h < 24) return "opened " + h + " hour" + (h === 1 ? "" : "s") + " ago";
  const d = Math.floor(h / 24);
  return d === 1 ? "opened yesterday" : "opened " + d + " days ago";
}

// What has happened for one open time, newest first: calls, automatic texts,
// replies, "said no" and expired texts, from the offers' own timestamps.
// nameOf(entryId) gives the patient name shown beside each line.
export function contactLog(offers, nameOf) {
  const out = [];
  (offers || []).forEach((o) => {
    const who = nameOf(o.waitlist_entry_id);
    if (o.contacted_at) out.push({ at: o.contacted_at, kind: "called", text: who + " was called" });
    if (o.cascade_sent_at) out.push({ at: o.cascade_sent_at, kind: "texted", text: "Automatic text sent to " + who });
    if (o.inbound_reply_at) out.push({ at: o.inbound_reply_at, kind: "replied", text: who + " replied by text" });
    if (o.status === "Declined") out.push({ at: o.declined_at || null, kind: "declined", text: who + " said no" });
    if (o.status === "Expired") out.push({ at: o.cascade_expires_at || null, kind: "expired", text: who + " did not answer the text in time" });
  });
  return out.sort((a, b) => new Date(b.at || 0) - new Date(a.at || 0));
}

// One read of future visits feeds both the open times (Cancelled or
// Rescheduled) and the "rebooked?" check (anything else at that time).
// PostgREST returns at most `cap` rows, soonest first; when the read is full,
// an open time after the last visit returned cannot be checked for a
// rebooking, so it is left out rather than shown as open by mistake.
export function splitFutureVisits(rows, cap) {
  const list = rows || [];
  const isOpen = (a) => (a.status === "Cancelled" || a.status === "Rescheduled") && a.cancelled_reason !== UNDONE_BOOKING_REASON;
  let slots = list.filter(isOpen);
  const booked = list.filter((a) => holds(a.status));
  const full = list.length >= cap;
  if (full && list.length) {
    const last = new Date(list[list.length - 1].start_at).getTime();
    slots = slots.filter((a) => new Date(a.start_at).getTime() < last);
  }
  return { slots, booked, full };
}
